import StyledHeading from '@/components/StyledHeading'
import { GREETING_DESKTOP, vipHeading } from '@/constants/content'
import SearchFormProvider from '@/context/SearchFormContext'
import useBootstrapData from '@/hooks/useBootstrapData'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE, ProductType } from '@/types'
import { Box, Card } from 'pcln-design-system'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import useHeadlineValues from '../SearchForm/customHooks/useHeadlineValues'
import Tabs from '../SearchForm/Tabs'
import SearchFormNewHorizonTabs from './SearchFormNewHorizonTabs'
import getProductCategory from './utils/getProductCategory'

const StyledCard = styled(Card)`
  border-radius: 24px 24px 6px 6px;
`

export default function SearchFormExperimentWrapper({
  currentTab,
  onFormChange,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const [activeTab, setActiveTab] = useState(currentTab)
  const {
    customer: { firstName: firstNameBootstrap },
    signInStatus: { signedIn: serverSideSignedIn },
  } = useBootstrapData()

  const { firstName: clientSideFirstName, signedIn: clientSideSignedIn } = useClientSideSignedInData()

  const firstName = serverSideSignedIn
    ? firstNameBootstrap
    : clientSideSignedIn
      ? clientSideFirstName
      : undefined

  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const headlineValues = useHeadlineValues(currentTab)

  const headlineWithName = firstName ? vipHeading.headlineWithName(firstName) : GREETING_DESKTOP
  const defaultHeadline = firstName ? headlineWithName : headlineValues.hotels.headline
  const hotelHeadline = prePopHeadline || SEMHeadline || defaultHeadline
  const updatedHeadlineValues = useMemo(
    () => ({
      ...headlineValues,
      hotels: {
        ...headlineValues.hotels,
        headline: hotelHeadline,
      },
    }),
    [hotelHeadline, headlineValues],
  )

  const formState = useMemo(
    () => getProductCategory(activeTab, updatedHeadlineValues, setLocation, true),
    [activeTab, updatedHeadlineValues],
  )

  const handleOnTabClick = (productType: ProductType) => {
    if (productType === activeTab) {
      return
    }

    if (productType === 'ai_mode') {
      analytics.fireGA4Event({
        event: 'ai_search',
        attributes: {
          type: 'TAB',
          application_code: 'DESKTOP',
          product_name: 'penny',
        },
      })
      window.open('/penny', '_blank')
      return
    }

    setActiveTab(productType)
    onFormChange(productType)
  }

  return (
    <ConditionalRender experimentCode='HP_SEARCH_FORM_WITH_TABS_OUTSIDE_CARD_DESKTOP'>
      <Original>
        <Box>
          <StyledHeading
            pl={0}
            pb={[16, null, null, null, 4]}
            pr={[0, null, null, null, '130px']}
            tag='h1'
            color='text.heading'
            text={formState.headline}
            mx={[4, null, null, null, 0]}
            textStyle={['heading2', 'heading3', null, 'heading2', null, 'heading1']}
          />
          <Card
            boxShadowSize='xl'
            borderWidth={0}
            borderRadius='2xl'
            minWidth={[null, null, null, '800px']}
            color='background.lightest'
            mx={[4, null, null, null, 0]}
          >
            <StyledCard borderWidth={0}>
              <Box pt={3}>
                <Tabs initialActiveTab={currentTab} onTabClick={handleOnTabClick} />
              </Box>
            </StyledCard>
            <SearchFormProvider>
              <Box p={3} id={`panel-${activeTab}`}>
                {formState.component}
              </Box>
              <ConditionalRender experimentCode='HP_SEARCH_FORM_WITHOUT_FOOTER_DESKTOP'>
                <Original>
                  <Box>{formState.bumper}</Box>
                </Original>
              </ConditionalRender>
            </SearchFormProvider>
          </Card>
        </Box>
      </Original>
      <Experiment>
        <SearchFormNewHorizonTabs currentTab={currentTab} onFormChange={onFormChange} />
      </Experiment>
    </ConditionalRender>
  )
}
